import Modal from "@mui/material/Modal";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftBadge from "components/SoftBadge";

function RoleChangeRequestsModal({
  open,
  onClose,
  requests,
  onApprove,
  onReject,
}) {
  const pendingRequests = requests.filter((r) => r.status === "pending");

  const getRoleBadgeColor = (role) => {
    const colors = {
      admin: "error",
      airport: "info",
      nonprofit: "success",
      pilot: "warning",
      user: "secondary",
    };
    return colors[role] || "secondary";
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "warning",
      approved: "success",
      rejected: "error",
    };
    return colors[status] || "secondary";
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Card
        sx={{
          width: "90%",
          maxWidth: 700,
          maxHeight: "90vh",
          overflow: "auto",
          p: 2,
        }}
      >
        <SoftBox>
          <SoftBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <SoftBox>
              <SoftTypography variant="h5" fontWeight="bold">
                Role Change Requests
              </SoftTypography>
              <SoftTypography variant="caption" color="text">
                Manage user role change requests
              </SoftTypography>
            </SoftBox>
            <SoftButton
              variant="text"
              color="secondary"
              iconOnly
              onClick={onClose}
            >
              <Icon>close</Icon>
            </SoftButton>
          </SoftBox>

          <Divider />

          <SoftBox mt={3}>
            {requests.length === 0 ? (
              <SoftBox textAlign="center" py={5}>
                <Icon fontSize="large" color="secondary">
                  inbox
                </Icon>
                <SoftTypography variant="body2" color="text" mt={2}>
                  No role change requests at this time
                </SoftTypography>
              </SoftBox>
            ) : (
              <List>
                {requests.map((request, index) => (
                  <ListItem
                    key={request.id}
                    sx={{
                      borderBottom:
                        index < requests.length - 1
                          ? "1px solid #f0f0f0"
                          : "none",
                      px: 0,
                      py: 2,
                    }}
                  >
                    <SoftBox width="100%">
                      <SoftBox
                        display="flex"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        mb={1}
                      >
                        <SoftBox>
                          <SoftTypography variant="button" fontWeight="bold">
                            {request.userName}
                          </SoftTypography>
                          <SoftBox
                            display="flex"
                            alignItems="center"
                            gap={1}
                            mt={0.5}
                          >
                            <SoftBadge
                              variant="contained"
                              badgeContent={request.currentRole}
                              color={getRoleBadgeColor(request.currentRole)}
                              size="xs"
                              container
                            />
                            <Icon fontSize="small" color="secondary">
                              arrow_forward
                            </Icon>
                            <SoftBadge
                              variant="contained"
                              badgeContent={request.requestedRole}
                              color={getRoleBadgeColor(request.requestedRole)}
                              size="xs"
                              container
                            />
                          </SoftBox>
                        </SoftBox>
                        <SoftBadge
                          variant="contained"
                          badgeContent={request.status}
                          color={getStatusColor(request.status)}
                          size="xs"
                          container
                        />
                      </SoftBox>

                      <SoftTypography variant="caption" color="text">
                        Requested: {request.requestedAt}
                      </SoftTypography>

                      {/* Information based on requested role */}
                      {request.requestedRole === "Pilot" &&
                        request.status === "pending" && (
                          <SoftBox
                            bgColor="warning"
                            variant="gradient"
                            borderRadius="md"
                            p={1.5}
                            mt={1}
                          >
                            <SoftBox display="flex" alignItems="center">
                              <Icon
                                fontSize="small"
                                sx={{ color: "white", mr: 1 }}
                              >
                                info
                              </Icon>
                              <SoftTypography variant="caption" color="white">
                                Pilots should download the mobile app after
                                approval
                              </SoftTypography>
                            </SoftBox>
                          </SoftBox>
                        )}

                      {(request.requestedRole === "NonProfit" ||
                        request.requestedRole === "Airport") &&
                        request.status === "pending" && (
                          <SoftBox
                            bgColor="info"
                            variant="gradient"
                            borderRadius="md"
                            p={1.5}
                            mt={1}
                          >
                            <SoftTypography variant="caption" color="white">
                              They will become the first admin of their{" "}
                              {request.requestedRole} entity
                            </SoftTypography>
                          </SoftBox>
                        )}

                      {/* Action buttons for pending requests */}
                      {request.status === "pending" && (
                        <SoftBox display="flex" gap={1} mt={2}>
                          <SoftButton
                            variant="gradient"
                            color="success"
                            size="small"
                            onClick={() => onApprove(request.id)}
                          >
                            <Icon sx={{ mr: 0.5 }} fontSize="small">
                              check
                            </Icon>
                            Approve
                          </SoftButton>
                          <SoftButton
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={() => onReject(request.id)}
                          >
                            <Icon sx={{ mr: 0.5 }} fontSize="small">
                              close
                            </Icon>
                            Reject
                          </SoftButton>
                        </SoftBox>
                      )}
                    </SoftBox>
                  </ListItem>
                ))}
              </List>
            )}

            {pendingRequests.length === 0 && requests.length > 0 && (
              <SoftBox mt={2} p={2} bgColor="light" borderRadius="md">
                <SoftTypography
                  variant="caption"
                  color="text"
                  textAlign="center"
                >
                  All requests have been processed
                </SoftTypography>
              </SoftBox>
            )}
          </SoftBox>

          {/* Close Button */}
          <SoftBox mt={3} display="flex" justifyContent="flex-end">
            <SoftButton variant="gradient" color="secondary" onClick={onClose}>
              Close
            </SoftButton>
          </SoftBox>
        </SoftBox>
      </Card>
    </Modal>
  );
}

export default RoleChangeRequestsModal;
